import React, { useState, useRef, useEffect } from 'react';
import '../styles/SportGrid.css';
import '../styles/SportCarousel.css';
import SportIcon from './SportIcons';
import AppIcon from './icons/AppIcon';

const SPORTS = [
  { id: 'Football', label: 'Football', icon: 'match', quote: 'Talent without hard work is nothing.', author: 'Cristiano Ronaldo' },
  { id: 'Basketball', label: 'Basketball', icon: 'activity', quote: 'You have to be able to accept failure to get better.', author: 'LeBron James' },
  { id: 'Running', label: 'Running', icon: 'activity', quote: "I run every day. It's like medicine.", author: 'Jennifer Carpenter' },
  { id: 'Natation', label: 'Natation', icon: 'activity', quote: "Nothing is impossible. With so many people saying it couldn’t be done, all it takes is imagination.", author: 'Michael Phelps' },
  { id: 'Tennis', label: 'Tennis', icon: 'activity', quote: "You play the ball. You don't play the opponent.", author: 'Roger Federer' },
  { id: 'Cyclisme', label: 'Cyclisme', icon: 'activity', quote: 'A bad day on the mountain beats a good day in the office.', author: 'Mike Brcic' },
  { id: 'Autre', label: 'Autre', icon: 'target', quote: 'Relevez le défi et dépassez-vous.', author: '' }
];

export default function SportGrid({ selected, onSelect, showCarousel = true }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [touchStart, setTouchStart] = useState(null);

  const checkScroll = () => {
    if (scrollRef.current) {
      setCanScrollLeft(scrollRef.current.scrollLeft > 0);
      setCanScrollRight(scrollRef.current.scrollLeft < scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      const amount = 260;
      scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
      setTimeout(checkScroll, 400);
    }
  };

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) scroll(diff > 0 ? 'right' : 'left');
    setTouchStart(null);
  };

  if (!showCarousel) {
    return (
      <section className="sport-section">
        <div className="sport-inner">
          <header className="sport-header">
            <div className="sport-header-left"><AppIcon name="target" size={28} strokeWidth={1.8} /></div>
            <div>
              <h2 className="sport-title">Choisis ton sport</h2>
              <p className="sport-sub">Sélectionne ton activité et découvre tes défis</p>
            </div>
          </header>

          <div className="sport-grid">
            {SPORTS.map(s => (
              <button
                key={s.id}
                className={`sg-card ${selected === s.id ? 'active' : ''}`}
                onClick={() => onSelect(s.id)}
                aria-pressed={selected === s.id}
              >
                <div className="sg-icon" aria-hidden><SportIcon type={s.id} className="sg-svg" /></div>
                <div className="sg-label">{s.label}</div>
                <div className="sg-meta">{s.count ? `${s.count} défis` : ''}</div>
              </button>
            ))}
          </div>

          <div className="sport-note">
            {!selected ? (
              <span>Choisissez un sport pour voir vos défis</span>
            ) : (
              <span>Défis disponibles pour : <strong>{selected}</strong></span>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Fullscreen single-slide carousel
  const [index, setIndex] = useState(0);
  const slideCount = SPORTS.length;
  const containerRef = useRef(null);
  const startX = useRef(null);
  const deltaX = useRef(0);
  const [selectingId, setSelectingId] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index]);

  const goNext = () => setIndex((i) => (i + 1) % slideCount);
  const goPrev = () => setIndex((i) => (i - 1 + slideCount) % slideCount);

  const handlePointerDown = (e) => {
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
    deltaX.current = 0;
  };
  const handlePointerMove = (e) => {
    if (startX.current == null) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    deltaX.current = x - startX.current;
    // optional: update transform for drag effect
    if (containerRef.current) {
      containerRef.current.style.transition = 'none';
      containerRef.current.style.transform = `translateX(${ -index * 100 + (deltaX.current / window.innerWidth) * 100 }%)`;
    }
  };
  const handlePointerUp = () => {
    if (startX.current == null) return;
    const threshold = 60;
    if (deltaX.current > threshold) {
      goPrev();
    } else if (deltaX.current < -threshold) {
      goNext();
    }
    // reset
    if (containerRef.current) {
      containerRef.current.style.transition = '';
      containerRef.current.style.transform = '';
    }
    startX.current = null;
    deltaX.current = 0;
  };

  const handleChoose = (s) => {
    if (selectingId) return; // prevent double
    setSelectingId(s.id);
    // show morphing/check animation then call onSelect
    setTimeout(() => {
      setSelectingId(null);
      onSelect(s.id);
    }, 700);
  };

  return (
    <section className="sport-fullscreen">
      <div className="carousel-stage" role="region" aria-label="Choisir votre sport">
        <div
          className="slides"
          ref={containerRef}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          style={{ transform: `translateX(${ -index * 100 }%)` }}
        >
          {SPORTS.map((s, i) => (
            <div
              key={s.id}
              className={`slide ${i === index ? 'active' : ''}`}
              style={{ background: `linear-gradient(120deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02)), linear-gradient(90deg, rgba(10,99,255,${i===index?0.08:0}), rgba(139,92,246,${i===index?0.06:0}))` }}
            >
              <div className="slide-card">
                <div className="slide-icon"><SportIcon type={s.id} className="slide-svg" /></div>
                <h3 className="slide-title">{s.label}</h3>
                <p className="slide-quote">"{s.quote || (s.id === 'Football' ? 'Le football ne construit pas seulement des joueurs, il crée des légendes.' : s.id === 'Running' ? 'Chaque kilomètre te rapproche d\'une meilleure version de toi-même.' : s.id === 'Natation' ? 'Dans l\'eau, tu ne luttes contre personne sauf toi-même.' : 'Relevez le défi et dépassez-vous.')}"</p>
                {s.author && <div className="slide-author">{s.author}</div>}
                <div className="slide-actions">
                  <button
                    className={`btn primary ${selectingId === s.id ? 'selecting' : ''}`}
                    onClick={() => handleChoose(s)}
                    aria-disabled={selectingId && selectingId !== s.id}
                    disabled={!!selectingId && selectingId !== s.id}
                  >
                    {selectingId === s.id ? (
                      <>
                        <span>Choisir {s.label}</span>
                        <span className={`checkwrap primary-checked`} aria-hidden>✓</span>
                      </>
                    ) : (
                      <>
                        <span>Choisir {s.label}</span>
                        <span className="checkwrap" aria-hidden></span>
                      </>
                    )}
                  </button>
                  <button className="btn secondary" onClick={goNext}>Passer</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button className="nav prev" onClick={goPrev} aria-label="Précédent">‹</button>
        <button className="nav next" onClick={goNext} aria-label="Suivant">›</button>

        <div className="dots">
          {SPORTS.map((s, i) => (
            <button key={s.id} className={`dot ${i === index ? 'active' : ''}`} onClick={() => setIndex(i)} aria-label={`Aller à ${s.label}`}></button>
          ))}
        </div>
      </div>
    </section>
  );
}
